/**
 * @fileoverview Core application provider that orchestrates context providers
 * for theming, routing, authentication, notifications, and dialogs.
 */

import * as React from 'react';
import { createTheme, Theme } from '@mui/material/styles';
import { DialogsProvider } from '@/shared/hooks/DialogsProvider';
import { NotificationsProvider } from '@/shared/hooks/NotificationsProvider';
import { AuthenticationContext, BrandingContext, NavigationContext, RouterContext, SessionContext, WindowContext } from '../contexts';
import { AppThemeProvider } from './AppThemeProvider';
import type { AppTheme, Authentication, Branding, Navigate, NavigateOptions, Navigation, NavigationDividerItem, NavigationItem, NavigationPageItem, NavigationSubheaderItem, Router, Session } from '../types';

// Re-export types for consumers
export type { AppTheme, Authentication, Branding, Navigate, NavigateOptions, Navigation, NavigationDividerItem, NavigationItem, NavigationPageItem, NavigationSubheaderItem, Router, Session };

// Re-export contexts for consumers
export { AuthenticationContext, SessionContext };

export interface AppProviderProps {
  children: React.ReactNode;
  theme?: AppTheme;
  branding?: Branding | null;
  navigation?: Navigation;
  router?: Router;
  session?: Session | null;
  authentication?: Authentication | null;
  window?: Window;
  nonce?: string;
}

function createDefaultTheme(): Theme {
  return createTheme({
    cssVariables: { colorSchemeSelector: 'data-toolpad-color-scheme' },
    colorSchemes: { dark: true },
  });
}

function AppProvider(props: AppProviderProps): React.JSX.Element {
  const {
    children,
    theme = createDefaultTheme(),
    branding = null,
    navigation = [],
    router = null,
    authentication = null,
    session = null,
    window: appWindow,
    nonce,
  } = props;

  return (
    <WindowContext.Provider value={appWindow}>
      <AuthenticationContext.Provider value={authentication}>
        <SessionContext.Provider value={session}>
          <RouterContext.Provider value={router}>
            <AppThemeProvider theme={theme} window={appWindow} nonce={nonce}>
              <NotificationsProvider>
                <DialogsProvider>
                  <BrandingContext.Provider value={branding}>
                    <NavigationContext.Provider value={navigation}>
                      {children}
                    </NavigationContext.Provider>
                  </BrandingContext.Provider>
                </DialogsProvider>
              </NotificationsProvider>
            </AppThemeProvider>
          </RouterContext.Provider>
        </SessionContext.Provider>
      </AuthenticationContext.Provider>
    </WindowContext.Provider>
  );
}

export { AppProvider };
